import React, {Component} from "react";
import PropTypes from 'prop-types'
import {Subject} from "rxjs";
import _ from 'lodash'
import SoundBarView from "./components/SoundBarView";
import Information from "./components/Information";
import Controll from "./components/Controll";
import Progress from "./components/Progress";
import Options from "./components/Options";
import Download from "./components/Download";
import License from "./components/License";
import SoundPlayer from "./utils/SoundPlayer";
import analyticsInstance from "../analytics/analyticsInstance";

/**
 * IMPORTANT:
 * The SoundBar updates its own state every 32 milliseconds.
 */
class SoundBar extends Component {

  // Events

  closeButtonPressed$ = new Subject()

  nextButtonPressed$ = new Subject()

  previousButtonPressed$ = new Subject()

  trackEnded$ = new Subject()

  trackPaused$ = new Subject()

  trackStartedPlaying$ = new Subject()

  // ---

  isFetchingTrackAudionUrl = false

  constructor(props) {
    super(props)

    this.state = {
      track: props.track ? props.track : null,
      playingTrack: false,
      isFetchingTrackAudionUrl: false,
      songStarted: false
    }

    this.soundPlayer = this.makeSoundPlayerAndSubscribeToEvents()
    if (props.track) {
      this.playTrack(props.track);
    } 
  }

  makeSoundPlayerAndSubscribeToEvents = () => {
    const soundPlayer = new SoundPlayer();
    soundPlayer.trackEnded$.subscribe(this.handleSongEnd)
    soundPlayer.trackPaused$.subscribe(this.handleSongPaused)
    soundPlayer.trackStartedPlaying$.subscribe(this.handleSongStartedPlaying)
    return soundPlayer
  }

  handleSongEnd = () => {
    this.trackEnded$.next(this.makeEventData())
  }

  makeEventData = () => ({
    title: this.state.track.title,
    track: this.state.track,
    seconds: this.soundPlayer.getCurrentTrackPlaytimeInSeconds()
  })

  handleSongPaused = () => this.trackPaused$.next(this.makeEventData())

  handleSongStartedPlaying = () => {
    this.trackStartedPlaying$.next(this.makeEventData());
    if (!this.state.songStarted) {
      this.setState({songStarted: true}, () => this.pause())
    }
  }

  componentDidMount = () => {
    this.refreshInterval = setInterval(() => this.forceUpdate(), 32)
  }

  componentWillUnmount = () => {
    clearInterval(this.refreshInterval)
  }

  /**
   * Returns a Promise because initially, calling setTracks and playTrack together would cause playTrack to fail
   * because setTracks did not set the state immediatelly. The promise will resolve when the state is set correctly.
   */

  setTrack = track => new Promise(resolve => {
    this.setState({ track }, resolve(track))
  })

  playTrack = () => {

    const track = this.state.track

    try {
      analyticsInstance.playMusic(track.title, track.subtitle)
    } catch (e) {
      console.log(`Can't get analytics instance`)
    }

    if (!track) {
      throw new Error('Track is invalid')
    }

    this.setState({
      playingTrack: true,
    })

    this.fetchTrackAudioUrl(track)
      .then(audio => this.soundPlayer.play(audio))
  }

  fetchTrackAudioUrl = track => {
    this.isFetchingTrackAudionUrl = true
    return track.fetchAudioUrl()
      .finally(() => this.isFetchingTrackAudionUrl = false)
  }

  currentTrackHasId = id => {
    if (this.state.playingTrack && this.state.track) {
      return this.state.track.id === id
    }
    return false
  }

  currentTrackIsTitled = title => {
    if (this.state.playingTrack && this.state.track) {
      return this.state.track.title === title
    }
    return false
  }

  currentTrackHasProperties = properties => {
    if (this.state.playingTrack) {
      const entries = _.entries(properties)
      const playingTrackHasPropertyWithValue = ([key, value]) => this.state.track[key] === value
      return _.every(entries, playingTrackHasPropertyWithValue)
    }
    return false
  }

  pause = () => {
    this.soundPlayer.pause()
  }

  isPlaying = () => {
    return this.soundPlayer.isPlaying()
  }

  render = () => {
    return <SoundBarView show={true} soundPlayer={this.soundPlayer}>{this.makeSoundBarContent()}</SoundBarView>
  }

  stop = () => {
    this.closeButtonPressed$.next(this.makeEventData())
    this.soundPlayer.stop();
  }

  makeSoundBarContent = () => {

    const { track } = this.state

    if (track === null) {
      return null
    }

    return this.makeSoundBarContentAccordingToDeviceSize()
  }

  makeSoundBarContentAccordingToDeviceSize = () => {

    let index = 0
    let getIndex = () => index++

    /**
     * An index is provided to each component so React does not 
     * cry over collection components without a key
     */

    return [
        // this.makeTrackInformation(getIndex()),
        this.makeControlls(getIndex()),
        // this.makeOptionsButton(),
        this.makeProgress(getIndex()),
      ]
  }

  makeTrackInformation = index => {
    const { image, title, subtitle } = this.state.track
    return <Information
      key={index}
      imageSrc={image}
      title={title}
      subtitle={subtitle}
    />
  }

  makeControlls = index => {
    return <Controll
      key={index}
      onPlayClick={this.handleControllPlayClick}
      isPlaying={this.soundPlayer.isPlaying()}
      isDisabled={this.isPreparingSoundbar()}
    />
  }

  isPreparingSoundbar = () => this.soundPlayer.isUnavailable() || this.isFetchingTrackAudionUrl

  handleControllPlayClick = () => {
    if (this.soundPlayer.isPlaying()) {
      this.soundPlayer.pause()
    } else {
      this.soundPlayer.resume()
    }
  }

  makeProgress = index => {

    const { waveform } = this.state.track
    const playingTrackCurrentSeconds = this.soundPlayer.getCurrentTrackPlaytimeInSeconds()
    const playingTrackTotalSeconds = this.soundPlayer.getCurrentTrackDurationInSeconds()

    return <Progress
      id={index}
      large={this.props.large}
      imageSrc={waveform}
      currentSeconds={playingTrackCurrentSeconds}
      totalSeconds={playingTrackTotalSeconds}
      onManualChange={this.handleProgressManualChange(playingTrackTotalSeconds)}
      isLoading={this.isPreparingSoundbar()}
    />
  }

  handleProgressManualChange = playingTrackTotalSeconds => sliderValue => {

    const second = playingTrackTotalSeconds * sliderValue

    this.soundPlayer.playCurrentTrackAtSecond(second)
  }

  makeDowloadButton = () => {

    const { texts } = this.props
    const { download } = texts
    const { onDownload } = this.state.track

    return onDownload && <Download key={this.state.track && this.state.track.id} onClick={onDownload} tooltip={download} />
  }

  makeLicenseButton = () => {

    const { texts } = this.props
    const { license } = texts
    const { onLicense } = this.state.track

    return onLicense && <License key={this.state.track && this.state.track.id} onClick={onLicense} label={license} />
  }

  makeOptionsButton = () => {

    const { texts } = this.props

    const {
      license,
      download,
      addToPlaylist,
    } = texts

    const {
      onLicense,
      onDownload,
      onAddToPlaylist
    } = this.state.track

    const options = []

    const pushOptionWhenActionIsDefined = (action, optionLabel) => action && options.push({ label: optionLabel, onClick: action })

    pushOptionWhenActionIsDefined(onLicense, license)
    pushOptionWhenActionIsDefined(onDownload, download)
    pushOptionWhenActionIsDefined(onAddToPlaylist, addToPlaylist)

    return options.length > 0 ? <Options key={this.state.track && this.state.track.id} options={options} /> : null
  }
}

export const propTypes = {
  texts: PropTypes.shape({
    license: PropTypes.string.isRequired,
    download: PropTypes.string.isRequired,
    addToPlaylist: PropTypes.string.isRequired,
    addToFavorites: PropTypes.string.isRequired,
    removeFromFavorites: PropTypes.string.isRequired,
  }).isRequired
}

SoundBar.propTypes = propTypes

export default SoundBar